/**
 * Cognito directo (amazon-cognito-identity-js), sin Amplify Auth.
 * Pool y client: las 4 env vía appConfig (con fallback a aws-exports).
 */
import {
  AuthenticationDetails,
  CognitoUser,
  CognitoUserAttribute,
  CognitoUserPool,
} from 'amazon-cognito-identity-js';
import { cognitoClientId, cognitoUserPoolId } from './appConfig';

const poolData = {
  UserPoolId: cognitoUserPoolId(),
  ClientId: cognitoClientId(),
};

let userPool = null;
let pendingNewPasswordUser = null;

function getUserPool() {
  if (!userPool) {
    userPool = new CognitoUserPool(poolData);
  }
  return userPool;
}

function cognitoUser(username) {
  return new CognitoUser({ Username: username, Pool: getUserPool() });
}

export function flattenCognitoAttributes(userData) {
  const attrs = {};
  for (const item of userData?.UserAttributes || []) {
    if (item?.Name) attrs[item.Name] = item.Value;
  }
  return attrs;
}

export function sessionToAuthUser(session, userData) {
  const idToken = session.getIdToken();
  const payload = idToken.decodePayload ? idToken.decodePayload() : idToken.payload || {};
  const attributes = flattenCognitoAttributes(userData);
  const username =
    userData?.Username ||
    payload['cognito:username'] ||
    payload.email ||
    '';
  return {
    user: {
      username,
      userId: payload.sub || '',
      idToken: {
        jwtToken: idToken.getJwtToken(),
        payload,
      },
    },
    attributes,
  };
}

function getSession(user) {
  return new Promise((resolve, reject) => {
    user.getSession((err, session) => {
      if (err) {
        reject(err);
        return;
      }
      resolve(session);
    });
  });
}

function getUserData(user) {
  return new Promise((resolve, reject) => {
    user.getUserData((err, data) => {
      if (err) {
        reject(err);
        return;
      }
      resolve(data);
    });
  });
}

export async function getCurrentSession({ forceRefresh = false } = {}) {
  const user = getUserPool().getCurrentUser();
  if (!user) return null;
  const session = await getSession(user);
  if (!session?.isValid() && !forceRefresh) return null;
  if (forceRefresh) {
    return new Promise((resolve, reject) => {
      user.refreshSession(session.getRefreshToken(), (err, next) => {
        if (err) {
          reject(err);
          return;
        }
        resolve(next);
      });
    });
  }
  return session;
}

export async function getIdToken() {
  const session = await getCurrentSession();
  return session ? session.getIdToken().getJwtToken() : null;
}

export async function getCurrentAuthUser() {
  const user = getUserPool().getCurrentUser();
  if (!user) return null;
  const session = await getSession(user);
  if (!session?.isValid()) return null;
  const userData = await getUserData(user);
  return sessionToAuthUser(session, userData);
}

export function signInWithChallenge(username, password) {
  const user = cognitoUser(username);
  const authDetails = new AuthenticationDetails({
    Username: username,
    Password: password,
  });

  return new Promise((resolve, reject) => {
    user.authenticateUser(authDetails, {
      onSuccess: () => {
        pendingNewPasswordUser = null;
        resolve({ needsNewPassword: false });
      },
      newPasswordRequired: () => {
        pendingNewPasswordUser = user;
        resolve({ needsNewPassword: true });
      },
      onFailure: (err) => {
        pendingNewPasswordUser = null;
        reject(err);
      },
    });
  });
}

export function completePendingNewPassword(newPassword) {
  if (!pendingNewPasswordUser) {
    return Promise.reject(
      new Error(
        'No hay un cambio de contraseña pendiente. Inicia sesión de nuevo.'
      )
    );
  }

  const user = pendingNewPasswordUser;
  return new Promise((resolve, reject) => {
    user.completeNewPasswordChallenge(
      newPassword,
      {},
      {
        onSuccess: () => {
          pendingNewPasswordUser = null;
          resolve();
        },
        onFailure: (err) => {
          reject(err);
        },
      }
    );
  });
}

export function signUp({ username, password, customerId, departments = '*' }) {
  const attributeList = [
    new CognitoUserAttribute({ Name: 'email', Value: username }),
    new CognitoUserAttribute({ Name: 'custom:hubspotid', Value: customerId }),
    new CognitoUserAttribute({
      Name: 'custom:departments',
      Value: departments,
    }),
  ];

  return new Promise((resolve, reject) => {
    getUserPool().signUp(username, password, attributeList, [], (err, result) => {
      if (err) {
        reject(err);
        return;
      }
      resolve(result);
    });
  });
}

export function resendSignUpCode(username) {
  const user = cognitoUser(username);
  return new Promise((resolve, reject) => {
    user.resendConfirmationCode((err, result) => {
      if (err) {
        reject(err);
        return;
      }
      resolve(result);
    });
  });
}

export function confirmSignUp(username, code) {
  const user = cognitoUser(username);
  return new Promise((resolve, reject) => {
    user.confirmRegistration(code, true, (err, result) => {
      if (err) {
        reject(err);
        return;
      }
      resolve(result);
    });
  });
}

export function resetPassword(username) {
  const user = cognitoUser(username);
  return new Promise((resolve, reject) => {
    user.forgotPassword({
      onSuccess: (data) => resolve(data),
      onFailure: (err) => reject(err),
    });
  });
}

export function confirmResetPassword(username, code, newPassword) {
  const user = cognitoUser(username);
  return new Promise((resolve, reject) => {
    user.confirmPassword(code, newPassword, {
      onSuccess: (data) => resolve(data),
      onFailure: (err) => reject(err),
    });
  });
}

export function signOut() {
  const user = getUserPool().getCurrentUser();
  if (user) user.signOut();
  pendingNewPasswordUser = null;
}

export function updateCognitoAttributes(attributes) {
  const user = getUserPool().getCurrentUser();
  if (!user) {
    return Promise.reject(new Error('No hay sesión activa'));
  }

  const list = Object.entries(attributes).map(
    ([Name, Value]) => new CognitoUserAttribute({ Name, Value: String(Value) })
  );

  return getSession(user).then(
    () =>
      new Promise((resolve, reject) => {
        user.updateAttributes(list, (err, result) => {
          if (err) {
            reject(err);
            return;
          }
          resolve(result);
        });
      })
  );
}

export function refreshCognitoSession() {
  const user = getUserPool().getCurrentUser();
  if (!user) {
    return Promise.reject(new Error('No hay sesión activa'));
  }

  return getSession(user).then(
    (session) =>
      new Promise((resolve, reject) => {
        user.refreshSession(session.getRefreshToken(), (err, newSession) => {
          if (err) {
            reject(err);
            return;
          }
          resolve(newSession);
        });
      })
  );
}
